/**
 * @file
 * BEF Number Dropdown behavior.
 *
 * Handles open/close toggling, an active-range badge, click-outside and
 * Escape closing, scroll-position preservation across AJAX refreshes, and
 * debounced form submission for the numeric range (min/max) exposed filter
 * rendered inside a dropdown panel.
 */
import { BREAKPOINTS } from '../../src/variables.ts';

((Drupal, once) => {
  'use strict';

  /**
   * Saved scroll position to restore after an AJAX refresh.
   */
  let savedScrollY: number | null = null;

  /**
   * Pending input values keyed by input `name`, re-applied to the fresh DOM
   * after an AJAX refresh so a value typed during an in-flight request is not
   * lost when Drupal rebuilds the form.
   */
  const pendingValues = new Map<string, string>();

  /**
   * Label of the dropdown the user last interacted with. Persists across AJAX
   * so the dropdown re-opens after results reload.
   */
  let lastInteractedLabel: string | null = null;

  /**
   * Desktop breakpoint media query — kept in sync with the SCSS `lg` breakpoint.
   */
  const desktopMq = window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`);

  /**
   * Returns the stable label text for a dropdown element.
   *
   * @param dropdown - The dropdown root element.
   */
  function getDropdownLabel(dropdown: HTMLElement): string {
    return dropdown.querySelector('.bef-number-dropdown__label')?.textContent?.trim() || '';
  }

  /**
   * Resets the `ajaxing` lock on any in-flight Drupal AJAX instances belonging
   * to the given form so a fresh submission is accepted.
   *
   * @param form - The exposed form element.
   */
  function unlockInFlightAjax(form: HTMLFormElement): void {
    const ajaxFn = (Drupal as unknown as Record<string, unknown>).ajax as
      { instances?: Array<{ element?: HTMLElement | false; ajaxing?: boolean } | null> } | undefined;

    if (!ajaxFn?.instances) return;

    ajaxFn.instances.forEach((instance) => {
      if (
        instance &&
        instance.element &&
        instance.element instanceof HTMLElement &&
        form.contains(instance.element) &&
        instance.ajaxing
      ) {
        instance.ajaxing = false;
      }
    });
  }

  /**
   * Triggers a form submission for the dropdown's form.
   *
   * @param dropdown - The dropdown element.
   */
  function triggerFormSubmit(dropdown: HTMLElement): void {
    const form = dropdown.closest('form') as HTMLFormElement | null;
    if (!form) return;

    unlockInFlightAjax(form);
    form.classList.add('views-exposed-form--loading');

    const submitBtn = form.querySelector<HTMLInputElement | HTMLButtonElement>(
      'input[type="submit"]:not([name="reset"]), button[type="submit"]:not([name="reset"])'
    );
    savedScrollY = window.scrollY;
    if (submitBtn) {
      submitBtn.click();
    } else if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  /**
   * Global click-outside handler registered once.
   * Closes any open desktop dropdown when clicking outside.
   */
  function handleDocumentClick(event: MouseEvent): void {
    if (!desktopMq.matches) return;

    document.querySelectorAll<HTMLElement>('[data-bef-number-dropdown]').forEach((dropdown) => {
      const trigger = dropdown.querySelector<HTMLButtonElement>('.bef-number-dropdown__trigger');
      if (trigger && trigger.getAttribute('aria-expanded') === 'true' && !dropdown.contains(event.target as Node)) {
        const label = getDropdownLabel(dropdown);
        // Keep open if an AJAX refresh triggered by this dropdown is pending.
        if (label && label === lastInteractedLabel) {
          return;
        }
        trigger.setAttribute('aria-expanded', 'false');
        dropdown
          .querySelector<HTMLElement>('.bef-number-dropdown__panel')
          ?.classList.add('bef-number-dropdown__panel--hidden');
      }
    });
  }

  // Register the global click-outside listener exactly once.
  document.addEventListener('click', handleDocumentClick);

  /**
   * Initializes BEF number dropdown widgets.
   *
   * @description
   * Manages the interactive dropdown that wraps a numeric range (min/max)
   * exposed filter, keeps an active-range badge in sync, debounces AJAX
   * submissions, and preserves the open state and scroll position across
   * Views AJAX refreshes.
   */
  Drupal.behaviors.tpBefNumberDropdown = {
    attach(context: Document | HTMLElement) {
      // Restore scroll position after an AJAX refresh.
      if (savedScrollY !== null) {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
        savedScrollY = null;
      }

      // Re-open the dropdown the user was interacting with. Drupal regenerates
      // element IDs on each AJAX refresh, so match by stable label text.
      if (lastInteractedLabel) {
        document.querySelectorAll<HTMLElement>('[data-bef-number-dropdown]').forEach((dropdown) => {
          if (getDropdownLabel(dropdown) === lastInteractedLabel) {
            const trigger = dropdown.querySelector<HTMLButtonElement>('.bef-number-dropdown__trigger');
            const panel = dropdown.querySelector<HTMLElement>('.bef-number-dropdown__panel');
            if (trigger && panel && trigger.getAttribute('aria-expanded') !== 'true') {
              trigger.setAttribute('aria-expanded', 'true');
              panel.classList.remove('bef-number-dropdown__panel--hidden');
            }
          }
        });
        lastInteractedLabel = null;
      }

      // Re-apply pending input values after an AJAX refresh.
      if (pendingValues.size > 0) {
        const root = context === document ? document.body : (context as HTMLElement);
        pendingValues.forEach((value, name) => {
          const input = root.querySelector<HTMLInputElement>(`input[name="${CSS.escape(name)}"]`);
          if (input) {
            if (input.value === value) {
              pendingValues.delete(name);
            } else {
              input.value = value;
            }
          }
        });
      }

      const dropdowns = once('tp-bef-number-dropdown', '[data-bef-number-dropdown]', context);

      dropdowns.forEach((el) => {
        const dropdown = el as HTMLElement;
        const trigger = dropdown.querySelector('.bef-number-dropdown__trigger') as HTMLButtonElement;
        const panel = dropdown.querySelector('.bef-number-dropdown__panel') as HTMLElement;
        const badge = dropdown.querySelector('[data-bef-number-dropdown-badge]') as HTMLElement;
        const inputs = Array.from(panel?.querySelectorAll<HTMLInputElement>('input[type="number"], input.bef-number'));
        const errorEl = dropdown.querySelector<HTMLElement>('[data-bef-number-dropdown-error]');
        const applyBtn = dropdown.querySelector<HTMLButtonElement>('.bef-number-dropdown__apply');

        if (!trigger || !panel) {
          return;
        }

        /**
         * Updates the badge with the number of active range bounds.
         */
        function updateBadge(): void {
          if (!badge) return;
          const minInput = panel.querySelector<HTMLInputElement>('.bef-number-dropdown__field--min input');
          const maxInput = panel.querySelector<HTMLInputElement>('.bef-number-dropdown__field--max input');
          const minVal = minInput?.value.trim() ?? '';
          const maxVal = maxInput?.value.trim() ?? '';
          const count = inputs.filter((input) => input.value.trim() !== '').length;
          const status = dropdown.querySelector<HTMLElement>('[data-bef-number-dropdown-status]');
          const anyEl = dropdown.querySelector<HTMLElement>('[data-bef-number-dropdown-status-any]');
          const selectedEl = dropdown.querySelector<HTMLElement>('[data-bef-number-dropdown-status-selected]');

          if (count > 0) {
            badge.textContent = String(count);
            badge.hidden = false;
            if (status) {
              let text: string;
              if (minVal && maxVal) {
                text = `${minVal} \u2013 ${maxVal}`;
              } else if (minVal) {
                text = `${minVal} \u2013`;
              } else {
                text = `\u2013 ${maxVal}`;
              }
              if (selectedEl) {
                selectedEl.textContent = text;
                selectedEl.hidden = false;
              }
              if (anyEl) anyEl.hidden = true;
              status.setAttribute('data-has-value', '');
            }
          } else {
            badge.textContent = '';
            badge.hidden = true;
            if (status) {
              if (anyEl) anyEl.hidden = false;
              if (selectedEl) {
                selectedEl.textContent = '';
                selectedEl.hidden = true;
              }
              status.removeAttribute('data-has-value');
            }
          }
        }

        /**
         * Validates that the minimum value does not exceed the maximum and vice
         * versa. Marks the inputs with aria-invalid, shows/hides the error
         * message, and enables/disables the Apply button accordingly.
         *
         * @returns True when the range is valid (or incomplete), false otherwise.
         */
        function validateRange(): boolean {
          const minInput = panel.querySelector<HTMLInputElement>('.bef-number-dropdown__field--min input');
          const maxInput = panel.querySelector<HTMLInputElement>('.bef-number-dropdown__field--max input');
          if (!minInput || !maxInput) return true;

          const minVal = minInput.value.trim();
          const maxVal = maxInput.value.trim();

          if (minVal !== '' && maxVal !== '') {
            const minNum = parseFloat(minVal);
            const maxNum = parseFloat(maxVal);

            if (!isNaN(minNum) && !isNaN(maxNum) && minNum > maxNum) {
              minInput.setAttribute('aria-invalid', 'true');
              maxInput.setAttribute('aria-invalid', 'true');
              if (errorEl) {
                errorEl.textContent = errorEl.dataset.i18nMinMaxError ?? '';
                errorEl.hidden = false;
              }
              return false;
            }
          }

          minInput.removeAttribute('aria-invalid');
          maxInput.removeAttribute('aria-invalid');
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.hidden = true;
          }
          return true;
        }

        /**
         * Measures the panel and flips it to right-anchored if it would
         * overflow the right edge of the viewport.
         */
        function alignPanel(): void {
          if (!desktopMq.matches) return;
          // Reset so we measure from the natural left-anchored position.
          panel.classList.remove('bef-number-dropdown__panel--flip');
          const rect = panel.getBoundingClientRect();
          if (rect.right > window.innerWidth - 8) {
            panel.classList.add('bef-number-dropdown__panel--flip');
          }
        }

        /**
         * Opens or closes the dropdown panel.
         *
         * @param open - Force open (true) or closed (false), or toggle if omitted.
         * @param focusFirst - Whether to move focus to the first input when opening.
         */
        function toggle(open?: boolean, focusFirst = false): void {
          const isOpen = open ?? trigger.getAttribute('aria-expanded') !== 'true';
          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          panel.classList.toggle('bef-number-dropdown__panel--hidden', !isOpen);

          if (isOpen) {
            alignPanel();
            if (focusFirst && inputs.length > 0) {
              inputs[0].focus();
            }
          }
        }

        // Toggle on trigger click.
        trigger.addEventListener('click', () => toggle(undefined, true));

        // Close on Escape (desktop only).
        dropdown.addEventListener('keydown', (event: KeyboardEvent) => {
          if (desktopMq.matches && event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
            toggle(false);
            trigger.focus();
          }
        });

        /**
         * Records the current state and remembers this dropdown so it re-opens
         * after the AJAX refresh.
         */
        function recordInteraction(): void {
          inputs.forEach((input) => {
            if (input.name) {
              pendingValues.set(input.name, input.value);
            }
          });
          savedScrollY = window.scrollY;
          const label = getDropdownLabel(dropdown);
          if (label) {
            lastInteractedLabel = label;
          }
        }

        // Prevent Views' own autosubmit from firing on input/change.
        panel.addEventListener('input', (event: Event) => {
          const input = event.target as HTMLInputElement;
          if (!inputs.includes(input)) return;
          event.stopPropagation();
        });

        // Validate the range when the user leaves a field, or attempts to
        // submit by pressing Enter inside one of the inputs.
        inputs.forEach((input) => {
          input.addEventListener('blur', () => validateRange());
          input.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !validateRange()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          });
        });

        // Stop the native change event from triggering an immediate submit.
        panel.addEventListener('change', (event: Event) => {
          if (inputs.includes(event.target as HTMLInputElement)) {
            event.stopPropagation();
          }
        });

        // Submit and update badge only when the Apply button is clicked.
        if (applyBtn) {
          applyBtn.addEventListener('click', () => {
            if (!validateRange()) return;
            updateBadge();
            recordInteraction();
            triggerFormSubmit(dropdown);
          });
        }

        // Set initial badge state.
        updateBadge();

        // Mobile: open the panel by default so all filters are visible.
        if (!desktopMq.matches) {
          toggle(true);
        }
      });
    },

    detach(_context: Document | HTMLElement, _settings: unknown, _trigger: string) {},
  };
})(Drupal, once);
